import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import { ASSETS } from "../../../utils/config";
import { colors } from "../../../utils";
import { useBreakpoint } from "../../../utils/breakpoints";
import { showCreateWheelPopup } from "../../../redux/reducers/createWheelPopup";
import {
  openDialogPopup,
  closeDialogPopup,
} from "../../../redux/reducers/dialogPopup";
import { openUpgradePopup } from "../../../redux/reducers/upgradePopup";
import { canCreateWheel } from "../../../services/api";
import {
  warnIfNearWheelCap,
  CREATED_WHEELS_CAP_KEY,
  CREATED_WHEELS_LIMIT_TEXT,
} from "../../../utils/wheelCapWarning";

/**
 * "Create Wheel" header CTA (Gordon 2026-08-18, option B).
 *
 * The shared GalleryHeader carries no Create-Wheel button; the Gallery page
 * creates wheels elsewhere. Discover (ExploreV2), however, kept a Create-Wheel
 * CTA in its old bespoke header, and Gordon wants that CTA KEPT when Discover
 * moves onto the shared header. Rather than fork the header, this is the CTA
 * extracted as an opt-in nav entry: GalleryHeader renders it only when its
 * `showCreateWheel` prop is set (Gallery leaves it off, byte-identical;
 * Discover passes it true).
 *
 * Behaviour is lifted verbatim from the retired ExploreV2Header: dispatch
 * `showCreateWheelPopup()`, responsive text->icon collapse below tablet-P via
 * the shared `useBreakpoint`, same AddWheelV2 glyph and accessible name.
 */
const CreateWheelNavEntry = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isPhone, isTabletP } = useBreakpoint();
  const iconOnly = isPhone || isTabletP;

  // paidlock-no-pre-wizard-cap-check: gate on the wheel-limit check before
  // opening the wizard, mirroring the canonical trigger used by the gallery's
  // own create control (FoldersV2/OwnerView.jsx handleCreateWheel). Without
  // this a Free user at the 5-wheel cap could complete all 7 wizard steps and
  // only then hit the server's 403 dead-end. A confirmed 403 shows the
  // upgrade upsell instead of opening the wizard; any other error fails open
  // (server 403 stays as the real backstop).
  const handleCreateWheel = async () => {
    try {
      const response = await canCreateWheel();
      if (response?.data?.status_code === 200) {
        // mkt-0920-dev-soft-cap-warnings: warn BEFORE the wizard opens when
        // the user is close to (but not yet at) their created-wheel cap.
        // Never blocks — the wizard opens either way.
        warnIfNearWheelCap(
          CREATED_WHEELS_CAP_KEY,
          "created Wheels",
          response?.data?.data,
          () => dispatch(openUpgradePopup({ requiredSubscription: 2 }))
        );
        dispatch(showCreateWheelPopup());
      }
    } catch (err) {
      if (err?.status === 403) {
        dispatch(
          openDialogPopup({
            isOpen: true,
            title: "Upgrade to Create More Wheels!",
            primaryButtonTitle: "Upgrade today!",
            secondaryButtonTitle: "Cancel",
            // hw-0914-dev-create-wheel-limit-copy-says-5-profile-says-83: the
            // BE cap (subscription/permissions.py CreateWheel) only counts
            // is_cloned=False wheels, but the profile "N Wheels" stat is
            // created+cloned (home/api/v1/viewsets.py number_of_wheels_count)
            // -- so a user with clones sees this dialog's bare "5 Wheels"
            // contradict a much larger profile count. "created" disambiguates.
            text: CREATED_WHEELS_LIMIT_TEXT,
            secondaryButtonOnClick: () => dispatch(closeDialogPopup()),
            primaryButtonOnClick: () => {
              dispatch(closeDialogPopup());
              navigate("/settings?tab=subscriptions");
            },
          })
        );
      } else {
        dispatch(showCreateWheelPopup());
      }
    }
  };

  const createGlyph = (
    <img src={ASSETS.segmentEditor.AddWheelV2} height={22} width={22} alt="" />
  );

  if (iconOnly) {
    return (
      <Tooltip title="Create Wheel">
        <IconButton
          aria-label="Create Wheel"
          className="create-wheel-button"
          onClick={handleCreateWheel}
          sx={{
            width: 44,
            height: 44,
            backgroundColor: colors.white,
            // gordon-0919-accent-create-wheel-wrong-button-header-vs-gallery:
            // this header CTA is product chrome (it also renders on Discover,
            // see the file docstring) -- a gallery's theme accent must never
            // restyle it, so it stays the plain brand purple. #5796 wrongly
            // wired --gt-create-wheel-border here instead of onto the
            // in-grid CreateWheelButton (FoldersV2/CreateWheelButton.jsx);
            // moved there, this reverts to the pre-#5796 literal color.
            border: `1px solid ${colors.PrimaryPurple}`,
            borderRadius: 1,
            margin: "0 10px",
            "&:hover": { backgroundColor: colors.white },
          }}
        >
          {createGlyph}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      className="create-wheel-button"
      aria-label="Create Wheel"
      // ISP-9999 (Gordon 2026-08-19, screenshot): too much internal padding
      // between the button's border and its icon/label. The outlined variant
      // had NO padding override here, so it fell through to MUI's default
      // outlined-button padding (5px 15px). Reuses "6px 10px" — the padding
      // already established by the sibling logged-out "Login Or Signup"
      // button a few lines down in GalleryHeader.jsx — rather than inventing
      // a new value, so this stays inside the existing in-file precedent.
      sx={{
        textTransform: "none",
        whiteSpace: "nowrap",
        padding: "6px 10px",
        minHeight: 44, // T22.716: 38.5px -> 44px touch floor
        margin: "0 10px",
        backgroundColor: colors.white,
        // gordon-0919-accent-create-wheel-wrong-button-header-vs-gallery:
        // no --gt-create-wheel-border here -- see the icon-only variant's
        // comment above. MUI's outlined variant otherwise derives this from
        // theme.palette.primary.main (colors.PrimaryPurple, see utils/
        // theme.js), unchanged from pre-#5796.
        "&:hover": {
          backgroundColor: colors.white,
        },
      }}
      color="primary"
      variant="outlined"
      onClick={handleCreateWheel}
      startIcon={createGlyph}
    >
      Create Wheel
    </Button>
  );
};

export default CreateWheelNavEntry;
